import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import Button from '@renderer/components/bahela/Button'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import BahelaRetakePage from '../bahela/Retake'

// Import Bahela assets
import bgLayer from '/assets/bahela/bg-layer.webp'
import bahelaLogo from '/assets/bahela/bahela.webp'

/**
 * Bahelakotak retake — dual-camera "kotak" variant of bahela/Retake.
 *
 * A kotak round always fires BOTH cameras, so photos come in pairs (left box + right box)
 * and the recapture loop (`bahelakotak/Capture.tsx`) only ever shoots full pairs. To keep
 * every retake round paired we:
 *   1. allow up to `maxRetakeCount * 2` selections (twice the single-camera cap), and
 *   2. require an EVEN, non-zero selection — the Retake button is disabled on an odd count
 *      so `retakeCount` (= selectedPhotos.length) feeding the capture loop is always even.
 *
 * Everything else mirrors bahela/Retake: unselected photos are kept, `retakeCount` +
 * `retakeSessionsCompleted` are stamped, and the user is sent back to `/capture` to reshoot.
 */
const KotakEvenRetake: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { i18n } = useTranslation()
  const [selectedPhotos, setSelectedPhotos] = useState<number[]>([])
  const [remainingTime, setRemainingTime] = useState<number>(60)

  const { config } = useConfigStore()
  const { gallery, setSessionState, uploadSessionState, userPreferences } = useSessionStateStore()

  // Kotak captures 2 photos per round → allow twice the single-camera cap.
  const baseRetakeCount = config?.pages.retake?.settings?.maxRetakeCount || 3
  const maxRetakeCount = baseRetakeCount * 2
  const retakeTimeout = config?.pages.retake?.settings?.retakeTimeout || 20

  // Initialize countdown hook
  const retakeCountdown = useCountdown(retakeTimeout)

  // Filter out only items with photoUrl
  const validPhotos = gallery.capturedItems.filter((item) => item.photoUrl)

  // A valid retake is a NON-ZERO, EVEN count (one full dual-camera pair per round).
  const selectedCount = selectedPhotos.length
  const isEvenSelection = selectedCount > 0 && selectedCount % 2 === 0
  const isOddSelection = selectedCount % 2 !== 0

  useEffect((): (() => void) => {
    retakeCountdown.setOnTick(() => {
      setRemainingTime(retakeCountdown.getRemainingSeconds())
    })

    retakeCountdown.setOnComplete(() => {
      continueToNextPage()
    })

    retakeCountdown.start()

    return () => {
      retakeCountdown.stop()
    }
  }, [])

  const handlePhotoClick = (index: number): void => {
    setSelectedPhotos((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index)
      } else if (prev.length < maxRetakeCount) {
        return [...prev, index]
      }
      return prev
    })
  }

  const handleRetake = async (): Promise<void> => {
    // Guard: only ever hand an even count to the dual-camera capture loop.
    if (!isEvenSelection) return

    const remainingItems = validPhotos.filter((_, index) => !selectedPhotos.includes(index))

    // Navigate to capture page for retakes
    navigate(`/${projectId}/capture`)

    // Update session state with remaining photos
    await setSessionState({
      userPreferences: {
        retakeCount: selectedPhotos.length,
        retakeSessionsCompleted: userPreferences.retakeSessionsCompleted + 1
      },
      gallery: {
        capturedItems: remainingItems
      }
    })

    // Upload session state before navigating
    uploadSessionState()
  }

  const continueToNextPage = async (): Promise<void> => {
    uploadSessionState()
    navigate(`/${projectId}/frame`)
  }

  // Call all hooks at the top level
  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedBahelaLogo = useCachedImage(bahelaLogo)

  return (
    <div className="relative bg-black w-full h-full overflow-hidden">
      {/* Background Layer */}
      <div
        className="absolute top-0 left-0 w-full h-full bg-cover z-[0]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      />

      {/* Main Content */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4/5 flex flex-col gap-[40px]">
        {/* Title */}
        <h1 className="font-serif-bahela text-center text-[60px] text-white">
          {i18n.language === 'id' ? 'Pilih ' : 'Choose '}
          <span className="text-uriYellow">
            {i18n.language === 'id'
              ? `jumlah genap (maks. ${maxRetakeCount}) Foto`
              : `an even number (max ${maxRetakeCount}) of photos`}
          </span>
          {i18n.language === 'id' ? ' untuk diulang' : ' to retake'}
        </h1>

        {/* Even-count hint when the current selection is odd */}
        <p className="font-body-bahela text-center text-[26px] text-uriYellow h-[32px] leading-none">
          {isOddSelection
            ? i18n.language === 'id'
              ? 'Pilih jumlah genap (2, 4, ...) — satu pasang kiri & kanan per pengulangan.'
              : 'Pick an even number (2, 4, ...) — one left & right pair per retake.'
            : ''}
        </p>

        {/* Photo Grid — responsive so 4…12 dual-camera shots all fit without overflow */}
        <div className="w-full grid grid-cols-4 gap-4 max-w-[900px] mx-auto place-items-center">
          {validPhotos.map((item, index) => {
            const isSelected = selectedPhotos.includes(index)
            return (
              <div key={index} className="relative w-full aspect-square">
                <div
                  className={`absolute inset-0 ${
                    isSelected ? 'border-[8px] rounded-xl border-uriYellow' : ''
                  }`}
                >
                  <CachedRetakePhoto
                    photoUrl={item.photoUrl}
                    index={index}
                    onPhotoClick={handlePhotoClick}
                  />
                </div>
                {isSelected && (
                  <div className="absolute bottom-4 right-4 drop-shadow-2xl w-[34px] h-[34px] rounded-full border-2 bg-grey border-uriYellow flex items-center justify-center z-10">
                    <span className="text-uriYellow text-xl">✓</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Action Buttons */}
        <div className="w-full flex gap-[46px] justify-center">
          <Button
            onClick={handleRetake}
            className={`text-[49px] z-[2] ${isEvenSelection ? 'opacity-100' : 'opacity-30'}`}
            textColor="methadone"
            disabled={!isEvenSelection}
          >
            {i18n.language === 'id' ? 'Foto Ulang' : 'Retake'}
          </Button>
          <Button
            onClick={continueToNextPage}
            className={`text-[49px] z-[2] ${selectedPhotos.length === 0 ? 'opacity-100' : 'opacity-30'}`}
            textColor="methadone"
            disabled={selectedPhotos.length > 0}
          >
            {i18n.language === 'id' ? 'Lanjut ' : 'Next '} ({remainingTime})
          </Button>
        </div>
      </div>

      {/* Bahela Logo */}
      <img
        src={cachedBahelaLogo}
        alt="Bahela Logo"
        className="absolute z-[1] bottom-[66px] left-[57px] w-[127px] h-[114px]"
      />
    </div>
  )
}

// Component for cached retake photos
const CachedRetakePhoto: React.FC<{
  photoUrl: string
  index: number
  onPhotoClick: (index: number) => void
}> = ({ photoUrl, index, onPhotoClick }) => {
  const cachedSrc = useCachedImage(photoUrl)
  return (
    <img
      src={cachedSrc}
      className="w-full h-full cursor-pointer btn-shadow rounded-[4px]"
      style={{ objectFit: 'cover' }}
      onClick={() => onPhotoClick(index)}
      alt={`Captured ${index + 1}`}
    />
  )
}

/**
 * Dispatcher: single-box (solo) sessions retake through the REGULAR bahela retake (select any
 * number, one camera); dual-box sessions use the kotak "×2, even count" retake. Matches the
 * capture-page dispatch so the two stay consistent for a given session.
 */
const RetakePage: React.FC = () => {
  const kotakSingleCamera = useSessionStateStore((s) => s.userPreferences.kotakSingleCamera)
  return kotakSingleCamera ? <BahelaRetakePage /> : <KotakEvenRetake />
}

export default RetakePage
